/**
 * Quick Schedule Templates Component
 * 
 * Provides one-click scheduling options with platform-specific peak times
 * and timezone-aware calculations.
 */

import React, { useMemo } from 'react';
import { 
    Button, 
    ButtonGroup, 
    Box, 
    Chip,
    Tooltip,
    Typography,
    Stack,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    WbSunny as MorningIcon,
    LunchDining as LunchIcon,
    NightsStay as EveningIcon,
    TrendingUp as PeakIcon,
    Schedule as ClockIcon,
    Psychology as SmartIcon,
    Star as OptimalIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useAuth } from '../../providers/AuthProvider';
import { getUserTimezone } from '../../utils/schedulingHelpers';
import { useOptimalTimes, useDataSufficiency } from '../../hooks/useOptimalTimes';
import { 
    formatOptimalTime, 
    getConfidenceBadge, 
    createScheduledDate,
    getNextOptimalTime,
    getRecommendationMessage,
    trackOptimalTimeUsage
} from '../../utils/optimalTimeHelpers';

// Extend dayjs with required plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * Platform-specific peak times based on industry research
 */
const PLATFORM_PEAK_TIMES = {
    twitter: [
        { hour: 9, minute: 0, label: 'Morning commute', icon: MorningIcon },
        { hour: 12, minute: 30, label: 'Lunch break', icon: LunchIcon },
        { hour: 17, minute: 30, label: 'Evening commute', icon: EveningIcon }
    ],
    facebook: [
        { hour: 9, minute: 0, label: 'Morning check', icon: MorningIcon },
        { hour: 13, minute: 0, label: 'Lunch scroll', icon: LunchIcon },
        { hour: 15, minute: 0, label: 'Afternoon break', icon: EveningIcon }
    ],
    instagram: [
        { hour: 11, minute: 0, label: 'Pre-lunch', icon: MorningIcon },
        { hour: 14, minute: 0, label: 'Post-lunch', icon: LunchIcon },
        { hour: 17, minute: 0, label: 'After work', icon: EveningIcon }
    ],
    linkedin: [
        { hour: 7, minute: 30, label: 'Before work', icon: MorningIcon },
        { hour: 12, minute: 0, label: 'Lunch networking', icon: LunchIcon },
        { hour: 17, minute: 0, label: 'End of workday', icon: EveningIcon }
    ],
    tiktok: [
        { hour: 10, minute: 0, label: 'Morning energy', icon: MorningIcon },
        { hour: 19, minute: 0, label: 'Prime time', icon: EveningIcon },
        { hour: 21, minute: 0, label: 'Late scroll', icon: EveningIcon }
    ],
    bluesky: [
        { hour: 9, minute: 0, label: 'Morning discourse', icon: MorningIcon },
        { hour: 13, minute: 0, label: 'Lunch chat', icon: LunchIcon },
        { hour: 20, minute: 0, label: 'Evening discussion', icon: EveningIcon }
    ],
    threads: [
        { hour: 8, minute: 0, label: 'Morning threads', icon: MorningIcon },
        { hour: 12, minute: 0, label: 'Lunch threads', icon: LunchIcon },
        { hour: 18, minute: 0, label: 'Evening threads', icon: EveningIcon }
    ],
    mastodon: [
        { hour: 9, minute: 0, label: 'Morning toot', icon: MorningIcon },
        { hour: 14, minute: 0, label: 'Afternoon toot', icon: LunchIcon },
        { hour: 20, minute: 0, label: 'Evening toot', icon: EveningIcon }
    ]
};

/**
 * Get the next occurrence of a specific time
 * @param {number} targetHour - Target hour (0-23)
 * @param {number} targetMinute - Target minute (0-59)
 * @param {string} userTz - User's timezone
 * @returns {dayjs} Next occurrence of the time
 */
const getNextOccurrence = (targetHour, targetMinute, userTz) => {
    const now = dayjs().tz(userTz);
    let target = now.hour(targetHour).minute(targetMinute).second(0);
    
    // If the time has passed today, move to tomorrow
    if (target.isSameOrBefore(now)) {
        target = target.add(1, 'day');
    }
    
    return target;
};

/**
 * Calculate optimal time based on selected platforms
 * @param {Array} selectedPlatforms - Array of platform names
 * @param {string} userTz - User's timezone
 * @returns {Object} Optimal time suggestion
 */
const calculateOptimalTime = (selectedPlatforms, userTz) => {
    if (selectedPlatforms.length === 0) {
        // Default to Twitter peak times if no platform selected
        return {
            time: getNextOccurrence(12, 30, userTz),
            label: 'Default peak time',
            confidence: 'low'
        };
    }
    
    if (selectedPlatforms.length === 1) {
        // Use the specific platform's peak times
        const platform = selectedPlatforms[0].toLowerCase();
        const peakTimes = PLATFORM_PEAK_TIMES[platform] || PLATFORM_PEAK_TIMES.twitter;
        
        // Find the next available peak time
        const now = dayjs().tz(userTz);
        for (const peak of peakTimes) {
            const peakTime = getNextOccurrence(peak.hour, peak.minute, userTz);
            if (peakTime.diff(now, 'minutes') >= 5) { // Must be at least 5 minutes in future
                return {
                    time: peakTime,
                    label: peak.label,
                    icon: peak.icon,
                    confidence: 'high'
                };
            }
        }
    }
    
    // Multiple platforms - find overlapping good times
    const allPeakHours = new Map();
    
    selectedPlatforms.forEach(platform => {
        const peaks = PLATFORM_PEAK_TIMES[platform.toLowerCase()] || [];
        peaks.forEach(peak => {
            const key = `${peak.hour}:${peak.minute}`;
            allPeakHours.set(key, (allPeakHours.get(key) || 0) + 1);
        });
    });
    
    // Find times that work for most platforms
    const sortedTimes = Array.from(allPeakHours.entries())
        .sort((a, b) => b[1] - a[1]);
    
    if (sortedTimes.length > 0) {
        const [timeStr, platformCount] = sortedTimes[0];
        const [hour, minute] = timeStr.split(':').map(Number);
        const optimalTime = getNextOccurrence(hour, minute, userTz);
        
        return {
            time: optimalTime,
            label: `Peak for ${platformCount}/${selectedPlatforms.length} platforms`,
            icon: PeakIcon,
            confidence: platformCount === selectedPlatforms.length ? 'high' : 'medium'
        };
    }
    
    // Fallback to general good time
    return {
        time: getNextOccurrence(12, 0, userTz),
        label: 'General peak time',
        icon: ClockIcon,
        confidence: 'low'
    };
};

/**
 * QuickSchedule Component
 */
const QuickSchedule = ({ 
    onSelectTime, 
    selectedPlatforms = [],
    disabled = false,
    showOptimal = true 
}) => {
    const { user } = useAuth();
    const userProfile = user?.[0]?.userprofile;
    const userTz = getUserTimezone(userProfile);
    
    // Get optimal times for the primary platform (first selected platform)
    const primaryPlatform = selectedPlatforms[0]?.toLowerCase();
    const { 
        optimalTimes, 
        loading: loadingOptimal, 
        error: optimalError
    } = useOptimalTimes(primaryPlatform);
    
    // Get data sufficiency information
    const { 
        dataSufficiency, 
        loading: loadingData 
    } = useDataSufficiency(primaryPlatform);
    
    // Determine data state using progressive disclosure logic
    const postCount = dataSufficiency?.total_posts || 0;
    const stage = dataSufficiency?.stage || 'no_data';
    const hasSufficientData = stage === 'sufficient_data';
    const hasMinimalData = stage === 'sparse_data' || stage === 'sufficient_data';
    const recommendationType = hasSufficientData ? 'personalized' : hasMinimalData ? 'hybrid' : 'industry';
    
    // Calculate quick options based on user timezone and optimal times
    const quickOptions = useMemo(() => {
        const now = dayjs().tz(userTz);
        const options = [];
        
        // Add optimal time suggestions if available and user has sufficient data
        if (showOptimal && optimalTimes.length > 0 && !loadingOptimal && hasSufficientData) {
            const nextOptimal = getNextOptimalTime(optimalTimes, now.toDate());
            if (nextOptimal) {
                const optimalDate = createScheduledDate(nextOptimal, now.toDate());
                const confidenceBadge = getConfidenceBadge(nextOptimal.confidence_score, nextOptimal.is_fallback);
                
                options.push({
                    label: 'Optimal Time',
                    time: dayjs(optimalDate),
                    value: dayjs(optimalDate).toISOString(),
                    icon: nextOptimal.is_fallback ? SmartIcon : OptimalIcon,
                    tooltip: `${formatOptimalTime(nextOptimal)} - ${nextOptimal.reason || 'Best engagement time'}`,
                    confidence: confidenceBadge.label,
                    confidenceColor: confidenceBadge.color,
                    isOptimal: true,
                    optimalData: nextOptimal
                });
            }
        }
        
        // Tomorrow 9am
        const tomorrow9am = now.add(1, 'day').hour(9).minute(0).second(0);
        options.push({
            label: 'Tomorrow 9am',
            time: tomorrow9am,
            value: tomorrow9am.toISOString(),
            icon: MorningIcon,
            tooltip: tomorrow9am.format('ddd, MMM D [at] h:mm A')
        });
        
        // Next Monday 10am
        let nextMonday = now.day(8).hour(10).minute(0).second(0); // day(8) = next Monday
        if (nextMonday.diff(now, 'days') > 7) {
            nextMonday = now.day(1).hour(10).minute(0).second(0); // This Monday if we're on Sunday
        }
        options.push({
            label: 'Next Monday',
            time: nextMonday,
            value: nextMonday.toISOString(),
            icon: ClockIcon,
            tooltip: nextMonday.format('ddd, MMM D [at] h:mm A')
        });
        
        // Peak Hours (calculated based on selected platforms) - show if optimal times are disabled or not available
        if (!showOptimal || (!optimalTimes.length || loadingOptimal || !hasSufficientData)) {
            const optimal = calculateOptimalTime(selectedPlatforms, userTz);
            const label = hasMinimalData ? 'Peak Hours' : 'Industry Peak';
            options.push({
                label: label,
                time: optimal.time,
                value: optimal.time.toISOString(),
                icon: optimal.icon || PeakIcon,
                tooltip: `${optimal.label} - ${optimal.time.format('ddd, MMM D [at] h:mm A')}`,
                confidence: optimal.confidence,
                isIndustryStandard: !hasSufficientData
            });
        }
        
        return options;
    }, [userTz, selectedPlatforms, showOptimal, optimalTimes, loadingOptimal, hasSufficientData, hasMinimalData]);
    
    // Handle optimal time selection with tracking
    const handleOptimalTimeSelect = (option) => {
        if (option.isOptimal && option.optimalData) {
            trackOptimalTimeUsage(option.optimalData, primaryPlatform, 'quick_schedule');
        }
        onSelectTime(option.value);
    };
    
    // Show loading state
    const isLoading = loadingOptimal || loadingData;
    
    return (
        <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                Quick Schedule
            </Typography>
            
            {/* Loading state for optimal times */}
            {isLoading && showOptimal && primaryPlatform && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                        Loading optimal times for {primaryPlatform}...
                    </Typography>
                </Box>
            )}
            
            {/* Error state for optimal times */}
            {optimalError && showOptimal && (
                <Alert severity="info" sx={{ mb: 1, fontSize: '0.75rem' }}>
                    Using standard times. {optimalError}
                </Alert>
            )}
            
            {/* Data sufficiency info for sparse data users */}
            {!isLoading && showOptimal && primaryPlatform && hasMinimalData && !hasSufficientData && (
                <Alert severity="info" sx={{ mb: 1, fontSize: '0.75rem' }}>
                    📊 Building your profile... {dataSufficiency?.posts_needed_for_next_stage || 0} more posts for personalized times
                </Alert>
            )}
            
            <Stack direction="row" spacing={1} flexWrap="wrap">
                {quickOptions.map((option, idx) => (
                    <Tooltip key={idx} title={option.tooltip} arrow>
                        <Box>
                            <Button
                                variant={option.isOptimal ? "contained" : "outlined"}
                                size="small"
                                onClick={() => handleOptimalTimeSelect(option)}
                                disabled={disabled}
                                startIcon={<option.icon fontSize="small" />}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    position: 'relative',
                                    ...(option.isOptimal && {
                                        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                        boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                                    }),
                                    ...(option.isIndustryStandard && {
                                        borderStyle: 'dashed',
                                        opacity: 0.8
                                    }),
                                    '&:hover': {
                                        backgroundColor: option.isOptimal ? 'primary.dark' : 'primary.main',
                                        color: 'primary.contrastText',
                                        borderColor: 'primary.main'
                                    }
                                }}
                            >
                                {option.label}
                                {option.confidence && (
                                    <Chip 
                                        label={option.confidence}
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            height: 16,
                                            fontSize: '0.65rem',
                                            backgroundColor: 
                                                option.confidenceColor === 'success' ? 'success.main' :
                                                option.confidenceColor === 'primary' ? 'primary.main' :
                                                option.confidenceColor === 'warning' ? 'warning.main' :
                                                option.confidenceColor === 'error' ? 'error.main' :
                                                option.confidence === 'high' ? 'success.main' :
                                                option.confidence === 'medium' ? 'warning.main' :
                                                'grey.500',
                                            color: 'white'
                                        }}
                                    />
                                )}
                                {option.isIndustryStandard && (
                                    <Chip 
                                        label="Standard"
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            height: 16,
                                            fontSize: '0.65rem',
                                            backgroundColor: 'grey.500',
                                            color: 'white'
                                        }}
                                    />
                                )}
                            </Button>
                        </Box>
                    </Tooltip>
                ))}
            </Stack>
            
            {/* Information about optimal times */}
            {showOptimal && primaryPlatform && !isLoading && (
                <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ display: 'block', mt: 1 }}
                >
                    {getRecommendationMessage(recommendationType, hasSufficientData, primaryPlatform)}
                </Typography>
            )}
            
            {/* Multi-platform info */}
            {selectedPlatforms.length > 1 && (
                <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ display: 'block', mt: 0.5 }}
                >
                    Times optimized for: {selectedPlatforms.join(', ')}
                </Typography>
            )}
        </Box>
    );
};

export default QuickSchedule; 